import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components';
import type { AppData, Transaction } from '../../data/types';
import { formatCurrency } from '../../lib/currency';
import { CHART_PALETTE } from './chartTheme';

type Props = {
  data: AppData;
  today: Date;
};

const MONTHS = 6;
const TOP_N = 5;
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function txDate(t: Transaction): Date {
  const [y, m, d] = t.date.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Top-5 spending categories tracked monthly across the last 6 months.
 * Surfaces which categories are growing or shrinking over time.
 */
export function CategoryTrends({ data, today }: Props) {
  const { rows, cats } = useMemo(() => {
    // Build month list
    const months: { key: string; label: string }[] = [];
    for (let i = MONTHS - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({ key: monthKey(d), label: SHORT_MONTH[d.getMonth()]! });
    }
    const monthIdx = new Map(months.map((m, i) => [m.key, i]));

    // Aggregate per category × month
    const totalByCat = new Map<string, number>();
    const matrix = new Map<string, number[]>();
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      const i = monthIdx.get(monthKey(txDate(t)));
      if (i === undefined) continue;
      totalByCat.set(t.categoryId, (totalByCat.get(t.categoryId) ?? 0) + t.amount);
      let arr = matrix.get(t.categoryId);
      if (!arr) {
        arr = new Array(MONTHS).fill(0);
        matrix.set(t.categoryId, arr);
      }
      arr[i] = (arr[i] ?? 0) + t.amount;
    }

    const topIds = [...totalByCat.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_N)
      .map(([id]) => id);

    const cats = topIds
      .map((id) => data.categories.find((c) => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c);

    const rows = months.map((m, i) => {
      const row: Record<string, number | string> = { label: m.label };
      for (const c of cats) row[c.id] = matrix.get(c.id)?.[i] ?? 0;
      return row;
    });
    return { rows, cats };
  }, [data, today]);

  const currency = data.profile.currency;

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <div className="mb-3">
        <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100">
          Category trends
        </h2>
        <p className="text-[13px] font-medium text-zinc-500">Last 6 months</p>
      </div>
      {cats.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-text-secondary">
          Not enough transaction history yet.
        </p>
      ) : (
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid
                vertical={false}
                stroke="rgb(var(--text-tertiary) / 0.15)"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#8E8E93', fontSize: 11 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#8E8E93', fontSize: 11 }}
                tickFormatter={(v: number) => `$${Math.round(v)}`}
              />
              <Tooltip
                separator=": "
                formatter={(v) => formatCurrency(Number(v) || 0, { currency })}
                contentStyle={{
                  background: 'rgb(var(--surface))',
                  border: '1px solid rgb(var(--surface-secondary))',
                  borderRadius: 12,
                  fontSize: 12,
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                iconSize={8}
              />
              {cats.map((c, i) => {
                const stroke = CHART_PALETTE[i % CHART_PALETTE.length]!;
                return (
                  <Line
                    key={c.id}
                    type="monotone"
                    dataKey={c.id}
                    name={c.name}
                    stroke={stroke}
                    strokeWidth={2}
                    dot={{ r: 3, fill: stroke }}
                    activeDot={{ r: 5 }}
                    animationDuration={750}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
